import type { ReportManager } from "@averagejoesff/shared";
import { formatOrdinal, formatRecord } from "../../lib/format";

interface MatchupHeaderProps {
  left: ReportManager | undefined;
  right: ReportManager | undefined;
}

/**
 * Both teams with their current-season form. On phones each team is its own
 * scoreboard row (logo, team name, then manager · titles · record) with a
 * "vs" divider between; from 600px up the two sides face off across a
 * central VS badge. Same markup either way — the layout is all CSS.
 */
export function MatchupHeader({ left, right }: MatchupHeaderProps) {
  return (
    <header className="matchup-header">
      <TeamSide manager={left} align="left" />
      <span className="matchup-header__vs" aria-label="versus">
        VS
      </span>
      <TeamSide manager={right} align="right" />
    </header>
  );
}

/** "2024" or "2019, 2023" — the seasons themselves while they fit, a count past that. */
function championshipLabel(seasons: readonly number[]): string {
  return seasons.length <= 2 ? seasons.join(", ") : `×${seasons.length}`;
}

function TeamSide({ manager, align }: { manager: ReportManager | undefined; align: "left" | "right" }) {
  if (!manager) return <div className={`matchup-header__side matchup-header__side--${align}`} />;

  const { record, rank, championships = [] } = manager;
  const form = [
    formatRecord(record.wins, record.losses, record.ties),
    rank !== null ? formatOrdinal(rank) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className={`matchup-header__side matchup-header__side--${align}`}>
      {manager.logoUrl ? (
        <img className="matchup-header__logo" src={manager.logoUrl} alt="" />
      ) : (
        // Holds the logo's slot so rows and sides line up whether or not Yahoo has a logo.
        <span className="matchup-header__logo matchup-header__logo--initial" aria-hidden="true">
          {Array.from(manager.teamName.trim())[0] ?? "?"}
        </span>
      )}
      <div className="matchup-header__details">
        <div className="matchup-header__team">{manager.teamName}</div>
        <div className="matchup-header__meta">
          <span className="matchup-header__manager">
            {manager.name}
            {championships.length > 0 && (
              <span
                className="matchup-header__titles"
                title={`Champion: ${championships.join(", ")}`}
                aria-label={`${championships.length}-time champion (${championships.join(", ")})`}
              >
                🏆 {championshipLabel(championships)}
              </span>
            )}
          </span>
          <span className="matchup-header__form">{form}</span>
        </div>
      </div>
    </div>
  );
}
